<template>
  <div class="user-management">
    <v-card>
      <v-card-title>
        用户列表
        <v-spacer></v-spacer>
        <v-btn color="primary" class="mr-4 add-user-btn" @click="openAddUserDialog">
          <v-icon left>mdi-plus</v-icon>
          添加用户
        </v-btn>
      </v-card-title>
      <v-data-table :headers="headers" :items="users" :search="search" :loading="loading" dense
        class="custom-select-table elevation-1">
        <template v-slot:top>
          <v-text-field v-model="search" label="搜索" class="mx-4" prepend-icon="mdi-magnify"></v-text-field>
        </template>
        <template v-slot:item.is_active="{ item }">
          <v-chip :color="item.is_active ? 'green' : 'red'" :text-color="item.is_active ? '#e8f5e9' : '#ffebee'" small
            :class="item.is_active ? 'active-status' : 'inactive-status'">
            {{ item.is_active ? '启用' : '禁用' }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon small class="mr-2" @click="editUser(item)">
            mdi-pencil
          </v-icon>
          <v-icon small @click="deleteUser(item)">
            mdi-delete
          </v-icon>
        </template>
      </v-data-table>
    </v-card>

    <!-- 添加用户对话框 -->
    <v-dialog v-model="addUserDialog" max-width="600px"> <v-card>
        <v-card-title class="dialog-header">
          <span class="text-h5">添加新用户</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-form ref="form" v-model="valid" lazy-validation>
              <v-row>
                <v-col cols="12">
                  <v-text-field v-model="newUser.email" label="邮箱" :rules="emailRules" required></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="newUser.full_name" label="用户名" :rules="nameRules" required></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="newUser.organize" label="机构" :rules="organizeRules" required></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="newUser.password" label="密码" :rules="passwordRules" type="password"
                    required></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-switch v-model="newUser.is_active" label="启用账户" color="success"></v-switch>
                </v-col>
                <v-col cols="12">
                  <v-switch v-model="newUser.is_superuser" label="超级用户" color="warning"></v-switch>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer> <v-btn color="blue-grey lighten-5" class="primary-text" text
            @click="closeAddUserDialog">取消</v-btn>
          <v-btn color="primary" class="primary-btn white--text" text @click="addUser" :loading="addingUser"
            :disabled="!valid">保存</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 编辑用户对话框 -->
    <v-dialog v-model="editUserDialog" max-width="600px"> <v-card>
        <v-card-title class="dialog-header">
          <span class="text-h5">编辑用户</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-form ref="editForm" v-model="editValid" lazy-validation>
              <v-row>
                <v-col cols="12">
                  <v-text-field v-model="editedUser.email" label="邮箱" :rules="emailRules" required></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="editedUser.full_name" label="用户名" :rules="nameRules" required></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="editedUser.organize" label="机构" :rules="organizeRules" required></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="editedUser.password" label="密码" :rules="passwordEditRules" type="password"
                    placeholder="留空则不修改密码"></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-switch v-model="editedUser.is_active" label="启用账户" color="success"></v-switch>
                </v-col>
                <v-col cols="12">
                  <v-switch v-model="editedUser.is_superuser" label="超级用户" color="warning"></v-switch>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer> <v-btn color="blue-grey lighten-5" class="primary-text" text
            @click="closeEditUserDialog">取消</v-btn>
          <v-btn color="blue darken-2" class="primary-btn" text @click="updateUser" :loading="updatingUser"
            :disabled="!editValid">保存</v-btn>
        </v-card-actions>
      </v-card> </v-dialog>

    <!-- 删除确认对话框 -->
    <v-dialog v-model="deleteConfirmDialog" max-width="400px">
      <v-card>
        <v-card-title class="dialog-header warning-dialog">
          <span class="text-h5">确认删除</span>
        </v-card-title>
        <v-card-text class="pt-4">
          <p>您确定要删除用户 <strong>{{ userToDelete?.full_name }}</strong> 吗？</p>
          <p class="mt-2 error--text text--darken-1">此操作不可逆，删除后数据将无法恢复。</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer> <v-btn color="blue-grey lighten-5" class="cancel-btn" text
            @click="closeDeleteDialog">取消</v-btn>
          <v-btn color="red darken-1" text class="error-btn" @click="confirmDelete" :loading="deletingUser">确认删除</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 提示消息 -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" class="centered-snackbar">
      {{ snackbar.text }} <template v-slot:action="{ attrs }">
        <v-btn text class="snackbar-close-btn" v-bind="attrs" @click="snackbar.show = false">
          关闭
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from '@/axios'

export default {
  name: 'AdminUserManagement',
  data () {
    return {
      search: '',
      loading: false,
      headers: [
        { text: '用户ID', value: 'id' },
        { text: '用户名', value: 'full_name' },
        { text: '邮箱', value: 'email' },
        { text: '机构', value: 'organize' },
        { text: '状态', value: 'is_active' },
        { text: '操作', value: 'actions', sortable: false }
      ],
      users: [],
      // 添加用户相关数据
      addUserDialog: false,
      addingUser: false,
      valid: true,
      newUser: {
        email: '',
        full_name: '',
        organize: '',
        password: '',
        is_active: true,
        is_superuser: false
      },
      // 编辑用户相关数据
      editUserDialog: false,
      updatingUser: false,
      editValid: true,
      editedUser: {
        id: '',
        email: '',
        full_name: '',
        organize: '',
        password: '',
        is_active: true,
        is_superuser: false
      },
      // 删除用户相关数据
      deleteConfirmDialog: false,
      deletingUser: false,
      userToDelete: null,
      // 验证规则
      emailRules: [
        v => !!v || '邮箱是必填项',
        v => /.+@.+\..+/.test(v) || '邮箱格式不正确'
      ],
      nameRules: [
        v => !!v || '用户名是必填项',
        v => (v && v.length >= 3) || '用户名至少需要3个字符'
      ],
      organizeRules: [
        v => !!v || '机构是必填项'
      ],
      passwordRules: [
        v => !!v || '密码是必填项',
        v => (v && v.length >= 8) || '密码至少需要8个字符'
      ],
      passwordEditRules: [
        v => !v || (v && v.length >= 8) || '密码至少需要8个字符'
      ],
      // 提示消息
      snackbar: {
        show: false,
        text: '',
        color: 'success'
      }
    }
  },
  created () {
    this.fetchUsers()
  },
  methods: {
    async fetchUsers () {
      this.loading = true
      try {
        const response = await axios.get('/users/')
        if (response.data && response.data.data) {
          this.users = response.data.data
        }
      } catch (error) {
        console.error('Error fetching users:', error)
      } finally {
        this.loading = false
      }
    },
    // 打开添加用户对话框
    openAddUserDialog () {
      this.newUser = {
        email: '',
        full_name: '',
        organize: '',
        password: '',
        is_active: true,
        is_superuser: false
      }
      this.addUserDialog = true
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },
    // 关闭添加用户对话框
    closeAddUserDialog () {
      this.addUserDialog = false
    },
    // 添加新用户
    async addUser () {
      if (this.$refs.form.validate()) {
        this.addingUser = true
        try {
          const response = await axios.post('/users/', this.newUser)
          if (response.data) {
            this.snackbar = {
              show: true,
              text: '用户添加成功',
              color: 'success'
            }
            this.addUserDialog = false
            this.fetchUsers() // 刷新用户列表
          }
        } catch (error) {
          console.error('Error adding user:', error)
          let errorMessage = '添加用户失败'
          if (error.response && error.response.data && error.response.data.detail) {
            errorMessage = error.response.data.detail
          }
          this.snackbar = {
            show: true,
            text: errorMessage,
            color: 'error'
          }
        } finally {
          this.addingUser = false
        }
      }
    },
    // 打开编辑用户对话框
    editUser (user) {
      this.editedUser = { ...user, password: '' }
      this.editUserDialog = true
      if (this.$refs.editForm) {
        this.$refs.editForm.resetValidation()
      }
    },
    // 关闭编辑用户对话框
    closeEditUserDialog () {
      this.editUserDialog = false
    },
    // 更新用户信息
    async updateUser () {
      if (this.$refs.editForm.validate()) {
        this.updatingUser = true
        try {
          const userData = { ...this.editedUser }
          // 如果密码为空，则不发送密码字段
          if (!userData.password) {
            delete userData.password
          }
          const response = await axios.patch(`/users/${this.editedUser.id}/`, userData)
          if (response.data) {
            this.snackbar = {
              show: true,
              text: '用户更新成功',
              color: 'success'
            }
            this.editUserDialog = false
            this.fetchUsers() // 刷新用户列表
          }
        } catch (error) {
          console.error('Error updating user:', error)
          let errorMessage = '更新用户失败'
          if (error.response && error.response.data && error.response.data.detail) {
            errorMessage = error.response.data.detail
          }
          this.snackbar = {
            show: true,
            text: errorMessage,
            color: 'error'
          }
        } finally {
          this.updatingUser = false
        }
      }
    },
    // 删除用户
    deleteUser (user) {
      this.userToDelete = user
      this.deleteConfirmDialog = true
    },
    // 关闭删除对话框
    closeDeleteDialog () {
      this.deleteConfirmDialog = false
      this.userToDelete = null
    },
    // 确认删除用户
    async confirmDelete () {
      if (!this.userToDelete) return
      this.deletingUser = true
      try {
        await axios.delete(`/users/${this.userToDelete.id}/`)
        this.snackbar = {
          show: true,
          text: '用户删除成功',
          color: 'success'
        }
        this.fetchUsers() // 刷新用户列表
      } catch (error) {
        console.error('Error deleting user:', error)
        let errorMessage = '删除用户失败'
        if (error.response && error.response.data && error.response.data.detail) {
          errorMessage = error.response.data.detail
        }
        this.snackbar = {
          show: true,
          text: errorMessage,
          color: 'error'
        }
      } finally {
        this.deletingUser = false
        this.deleteConfirmDialog = false
        this.userToDelete = null
      }
    }
  }
}
</script>

<style scoped></style>
